import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { 
  Bell, ArrowLeft, Save, AlertTriangle, Moon, 
  Dumbbell, Apple, Heart, Clock, Mail, Smartphone,
  BellRing, BellOff, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const AlertSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Alert settings state
  const [settings, setSettings] = useState({
    // Notification channels
    email_notifications: true,
    push_notifications: true,
    
    // Recovery alerts
    recovery_alerts_enabled: true,
    recovery_threshold: 60,
    
    // Workout alerts
    missed_workout_alerts: true,
    missed_workout_days: 2,
    
    // Nutrition alerts
    nutrition_alerts_enabled: false,
    missed_meals_threshold: 2,
    
    // Sleep alerts
    sleep_alerts_enabled: true,
    min_sleep_hours: 7,
    
    // Quiet hours
    quiet_hours_enabled: false,
    quiet_start: '22:00',
    quiet_end: '07:00'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/supporter/alert-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Settings might not exist yet, use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/supporter/alert-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alert settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-aura-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-aura-text-muted hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-aura-secondary" />
              <span className="font-heading font-bold text-lg text-white">Alert Settings</span>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
            data-testid="save-alert-settings-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        {/* Info Banner */}
        <Card className="bg-aura-secondary/10 border-aura-secondary/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <BellRing className="w-5 h-5 text-aura-secondary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-white">Stay Informed About Your Athletes</p>
                <p className="text-sm text-aura-text-muted mt-1">
                  Set up alerts to be notified when athletes need attention or miss their goals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Channels */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Notification Channels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="font-medium text-white">Email Notifications</p>
                  <p className="text-sm text-aura-text-muted">Receive alerts via email</p>
                </div>
              </div>
              <Switch
                checked={settings.email_notifications}
                onCheckedChange={(checked) => updateSetting('email_notifications', checked)}
                data-testid="email-notifications-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-green-400" />
                <div>
                  <p className="font-medium text-white">Push Notifications</p>
                  <p className="text-sm text-aura-text-muted">Browser & app notifications</p>
                </div>
              </div>
              <Switch
                checked={settings.push_notifications}
                onCheckedChange={(checked) => updateSetting('push_notifications', checked)}
                data-testid="push-notifications-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Recovery Alerts */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Moon className="w-5 h-5 text-cyan-400" />
                Recovery Alerts
              </CardTitle>
              <Switch
                checked={settings.recovery_alerts_enabled}
                onCheckedChange={(checked) => updateSetting('recovery_alerts_enabled', checked)}
                data-testid="recovery-alerts-switch"
              />
            </div>
          </CardHeader>
          {settings.recovery_alerts_enabled && (
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-aura-text-muted">
                    Alert when recovery score falls below
                  </Label>
                  <Badge className={`${
                    settings.recovery_threshold >= 70 ? 'bg-green-500/20 text-green-400' :
                    settings.recovery_threshold >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  } border-0`}>
                    {settings.recovery_threshold}/100
                  </Badge>
                </div>
                <Slider
                  value={[settings.recovery_threshold]}
                  onValueChange={([value]) => updateSetting('recovery_threshold', value)}
                  min={30}
                  max={90}
                  step={5}
                  className="mt-3"
                  data-testid="recovery-threshold-slider"
                />
                <div className="flex justify-between text-xs text-aura-text-subtle mt-1">
                  <span>More Alerts</span>
                  <span>Fewer Alerts</span>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Workout Alerts */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Dumbbell className="w-5 h-5 text-aura-primary" />
                Workout Alerts
              </CardTitle>
              <Switch
                checked={settings.missed_workout_alerts}
                onCheckedChange={(checked) => updateSetting('missed_workout_alerts', checked)}
                data-testid="workout-alerts-switch"
              />
            </div>
          </CardHeader>
          {settings.missed_workout_alerts && (
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-aura-text-muted">
                    Alert after missing workouts for
                  </Label>
                  <Badge className="bg-aura-primary/20 text-aura-primary border-0">
                    {settings.missed_workout_days} day{settings.missed_workout_days !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <Slider
                  value={[settings.missed_workout_days]}
                  onValueChange={([value]) => updateSetting('missed_workout_days', value)}
                  min={1}
                  max={7}
                  step={1}
                  className="mt-3"
                  data-testid="missed-workout-days-slider"
                />
                <div className="flex justify-between text-xs text-aura-text-subtle mt-1">
                  <span>1 day</span>
                  <span>7 days</span>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Sleep Alerts */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                Sleep Alerts
              </CardTitle>
              <Switch
                checked={settings.sleep_alerts_enabled}
                onCheckedChange={(checked) => updateSetting('sleep_alerts_enabled', checked)}
                data-testid="sleep-alerts-switch"
              />
            </div>
          </CardHeader>
          {settings.sleep_alerts_enabled && (
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-aura-text-muted">
                    Alert when sleep is less than
                  </Label>
                  <Badge className="bg-purple-500/20 text-purple-400 border-0">
                    {settings.min_sleep_hours} hours
                  </Badge>
                </div>
                <Slider
                  value={[settings.min_sleep_hours]}
                  onValueChange={([value]) => updateSetting('min_sleep_hours', value)}
                  min={5}
                  max={9}
                  step={0.5}
                  className="mt-3"
                  data-testid="min-sleep-slider"
                />
                <div className="flex justify-between text-xs text-aura-text-subtle mt-1">
                  <span>5 hrs</span>
                  <span>9 hrs</span>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Nutrition Alerts */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Apple className="w-5 h-5 text-green-400" />
                Nutrition Alerts
              </CardTitle>
              <Switch
                checked={settings.nutrition_alerts_enabled}
                onCheckedChange={(checked) => updateSetting('nutrition_alerts_enabled', checked)}
                data-testid="nutrition-alerts-switch"
              />
            </div>
          </CardHeader>
          {settings.nutrition_alerts_enabled && (
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-aura-text-muted">
                    Alert when meals missed per day exceeds
                  </Label>
                  <Badge className="bg-green-500/20 text-green-400 border-0">
                    {settings.missed_meals_threshold} meal{settings.missed_meals_threshold !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <Slider
                  value={[settings.missed_meals_threshold]}
                  onValueChange={([value]) => updateSetting('missed_meals_threshold', value)}
                  min={1}
                  max={4}
                  step={1}
                  className="mt-3"
                  data-testid="missed-meals-slider"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Quiet Hours */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <BellOff className="w-5 h-5 text-gray-400" />
                Quiet Hours
              </CardTitle>
              <Switch
                checked={settings.quiet_hours_enabled}
                onCheckedChange={(checked) => updateSetting('quiet_hours_enabled', checked)}
                data-testid="quiet-hours-switch"
              />
            </div>
          </CardHeader>
          {settings.quiet_hours_enabled && (
            <CardContent className="space-y-4">
              <p className="text-sm text-aura-text-muted">
                No notifications will be sent during these hours.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Start Time
                  </Label>
                  <Input
                    type="time"
                    value={settings.quiet_start}
                    onChange={(e) => updateSetting('quiet_start', e.target.value)}
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="quiet-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    End Time
                  </Label>
                  <Input
                    type="time"
                    value={settings.quiet_end}
                    onChange={(e) => updateSetting('quiet_end', e.target.value)}
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="quiet-end-input"
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Save Button (Mobile) */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-aura-secondary hover:bg-aura-secondary/90 text-white h-12 sm:hidden"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <Check className="w-5 h-5 mr-2" />
          )}
          Save Alert Settings
        </Button>
      </main>
    </div>
  );
};

export default AlertSettingsPage;
